/**
 * Add/edit a manual report entry (reports:write only). Follows shop/ui's
 * UserFormDialog conventions: open-resets-state effect, a submittingRef
 * double-submit guard, busy disables every field and blocks onClose, and a
 * pure module-level validate/build split (reportFormValidation.ts).
 *
 * Two server errors get their own inline treatment (E2 brief):
 *   - 409 report_conflict: a row already exists for this shop+date; steer the
 *     user to edit that row instead of retrying create.
 *   - 400 currency_code_required: the shop has never synced a currency, so
 *     one must be typed in; the error lands on the currency field itself.
 */
import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';

import { ApiError, type ReportRow, type ReportType, type ShopSummary } from '../../api/index.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';
import { REPORT_TYPE_OPTIONS } from './periodOptions.ts';
import {
  buildCreatePayload,
  buildUpdatePayload,
  initialReportForm,
  validateReportForm,
  type ReportFormState,
} from './reportFormValidation.ts';

export interface ReportFormDialogProps {
  open: boolean;
  report: ReportRow | null;
  shops: ShopSummary[];
  defaultReportType: ReportType;
  /** Shop-scoped callers preselect and lock the current shop. */
  defaultShopId?: number;
  lockShop?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/** True for the two server errors that get bespoke inline copy. */
function isReportConflict(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'report_conflict';
}
function isCurrencyRequired(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'currency_code_required';
}

export function ReportFormDialog({
  open,
  report,
  shops,
  defaultReportType,
  defaultShopId,
  lockShop = false,
  onClose,
  onSaved,
}: Readonly<ReportFormDialogProps>): React.JSX.Element {
  const { api } = useAuth();
  const isEdit = report !== null;
  const fullScreen = useFullScreenDialog();

  const [form, setForm] = useState<ReportFormState>(() => initialReportForm(report, defaultReportType, defaultShopId));
  const [error, setError] = useState('');
  const [currencyError, setCurrencyError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setForm(initialReportForm(report, defaultReportType, defaultShopId));
      setError('');
      setCurrencyError('');
      setBusy(false);
      submittingRef.current = false;
    }
  }, [open, report, defaultReportType, defaultShopId]);

  const set = <K extends keyof ReportFormState>(key: K, value: ReportFormState[K]) => {
    setError('');
    setCurrencyError('');
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async () => {
    if (submittingRef.current) return;
    const problem = validateReportForm(form, isEdit);
    if (problem) {
      setError(problem);
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    setError('');
    setCurrencyError('');
    try {
      if (isEdit && report) {
        await api.reports.update(report.id, buildUpdatePayload(form));
      } else {
        await api.reports.create(buildCreatePayload(form));
      }
      onSaved();
      onClose();
    } catch (e) {
      if (isReportConflict(e)) {
        setError('A report already exists for this shop and date. Edit that row instead.');
      } else if (isCurrencyRequired(e)) {
        setCurrencyError('This shop has never synced a currency; enter one to continue.');
      } else {
        setError(actionError(e));
      }
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle>{isEdit ? 'Edit report' : 'Add report'}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl fullWidth disabled={busy || isEdit || lockShop}>
              <InputLabel id="report-shop-label">Shop</InputLabel>
              <Select
                labelId="report-shop-label"
                label="Shop"
                value={form.shopId === '' ? '' : String(form.shopId)}
                onChange={(e) => set('shopId', e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ 'aria-label': 'report-shop' }}
              >
                {shops.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.shop_code} - {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DatePicker
              label="Date"
              value={form.date === '' ? null : dayjs(form.date)}
              onChange={(d) => set('date', d?.isValid() ? d.format('YYYY-MM-DD') : '')}
              disabled={busy}
              slotProps={{ textField: { fullWidth: true, slotProps: { htmlInput: { 'aria-label': 'report-date' } } } }}
            />

            <TextField
              label="Gross sales"
              value={form.gross}
              onChange={(e) => set('gross', e.target.value)}
              fullWidth
              disabled={busy}
              slotProps={{ htmlInput: { 'aria-label': 'report-gross', inputMode: 'decimal' } }}
            />
            <TextField
              label="Payout"
              value={form.payout}
              onChange={(e) => set('payout', e.target.value)}
              fullWidth
              disabled={busy}
              slotProps={{ htmlInput: { 'aria-label': 'report-payout', inputMode: 'decimal' } }}
            />
            <TextField
              label="Tax"
              value={form.tax}
              onChange={(e) => set('tax', e.target.value)}
              fullWidth
              disabled={busy}
              slotProps={{ htmlInput: { 'aria-label': 'report-tax', inputMode: 'decimal' } }}
            />

            <FormControl fullWidth disabled={busy}>
              <InputLabel id="report-type-label">Report type</InputLabel>
              <Select
                labelId="report-type-label"
                label="Report type"
                value={form.reportType}
                onChange={(e) => set('reportType', e.target.value)}
                inputProps={{ 'aria-label': 'report-type' }}
              >
                {REPORT_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Currency code"
              value={form.currencyCode}
              onChange={(e) => set('currencyCode', e.target.value)}
              fullWidth
              disabled={busy}
              error={currencyError !== ''}
              helperText={currencyError || 'Needed only if the shop has never synced.'}
              slotProps={{ htmlInput: { 'aria-label': 'report-currency' } }}
            />

            {error && (
              <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
                {error}
              </Alert>
            )}
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button onClick={() => void submit()} disabled={busy}>
          {isEdit ? 'Save changes' : 'Create report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReportFormDialog;
